import { resolveApiUrl } from "../../../assets/GlobalVariables";
import { performPostFetch } from "../../../services/fetchService";
import { ORGANIZED_TOUR_INFO_POST_PATH } from "../constants/apiEndpoints";
import { attachAgentSessionToBody } from "./sessionPayload";

const _inflight = new Map();
/** Successful responses keyed by tourInfoKey — avoids repeat getOrgTourInfo for same deal in-session. */
const _completedByKey = new Map();

function tourInfoKey(payload) {
  const dealId = String(payload?.dealId ?? "").trim();
  const tourName = String(payload?.tourName ?? "").trim();
  const supplierId = String(payload?.supplierId ?? "").trim();
  const depDate = String(payload?.depDate ?? "").trim();
  const retDate = String(payload?.retDate ?? "").trim();
  return `${dealId}|${tourName}|${supplierId}|${depDate}|${retDate}`;
}

/**
 * POST getOrgTourInfo (+ server-side resource fetch). In-flight dedup per deal key.
 * No retries here — axios adapter dedupes identical in-flight only.
 *
 * @param {{ dealId: string, tourName: string, supplierId: string, depDate?: string, retDate?: string }} payload
 * @param {{ signal?: AbortSignal }} [opts]
 */
export async function getTourInfo(payload, opts = {}) {
  const key = tourInfoKey(payload);
  const cached = _completedByKey.get(key);
  if (cached != null) {
    if (import.meta.env?.DEV) {
      console.info("[ORG_TOUR_INFO_FETCH_SKIP_CACHE_HIT]", { key });
    }
    return cached;
  }
  const existing = _inflight.get(key);
  if (existing) {
    if (import.meta.env?.DEV) {
      console.info("[ORG_TOUR_INFO_FETCH_SKIP_DUPLICATE]", { key });
    }
    return existing;
  }

  const url = resolveApiUrl(ORGANIZED_TOUR_INFO_POST_PATH);
  const body = attachAgentSessionToBody({
    dealId: payload.dealId,
    tourName: payload.tourName,
    supplierId: payload.supplierId,
    ...(String(payload.depDate ?? "").trim() ? { depDate: String(payload.depDate).trim() } : {}),
    ...(String(payload.retDate ?? "").trim() ? { retDate: String(payload.retDate).trim() } : {}),
  });

  const axiosExtra = opts.signal ? { signal: opts.signal } : {};

  const promise = (async () => {
    try {
      const res = await performPostFetch(url, body, false, axiosExtra);
      const out = res?.data ?? res;
      if (out && typeof out === "object" && out.ok !== false) {
        _completedByKey.set(key, out);
      }
      return out;
    } catch (err) {
      const code = err?.code;
      const noResponse = !err?.response;
      if (
        import.meta.env?.DEV &&
        noResponse &&
        (code === "ECONNREFUSED" || code === "ERR_NETWORK" || code === "ECONNABORTED")
      ) {
        console.warn("[ORG_TOUR_INFO_PROXY_ERROR_STOP]", {
          key,
          code,
          message: err?.message,
        });
      }
      throw err;
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, promise);
  return promise;
}
